"use client";

import { useState } from "react";
import { Box, Typography, IconButton, styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
} from "@/ui";
import { SkillLogo } from "@components/Floats/parts/SkillLogo";
import Heading from "./parts/Heading";
import SkillSelect from "@components/shared/form/SkillSelect";

const SkillsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
  rowGap: "15px",
  transition: "800ms",
});

const Skill = styled(Box)({
  display: "flex",
  alignItems: "center",
  minHeight: "26px",
});

const Pill = styled(Box)({
  width: "90%",
  display: "flex",
  alignItems: "center",
  borderRadius: "9999px",
  border: "1px solid black",
  padding: "8px 23px 8px 13px",
  position: "relative",
  transition: "800ms",
  "&.new-item": { opacity: 0.5 },
});

const CloseButton = styled(IconButton)({
  display: "block",
  position: "absolute",
  right: 0,
  fontSize: "22px",
  "& svg": { color: "black", fontSize: "17px" },
});

const Description = styled(Typography)({
  fontSize: "12px",
  fontWeight: 500,
  marginLeft: "8px",
});

export default function EditSkills({ onClose, open, candidate, onAdd, onRemove }) {
  const [skills, setSkills] = useState(candidate?.skills || []);

  // Re-sync local editable state when the candidate's skills prop changes
  // (React's replacement for a set-state-in-effect).
  const [prevCandidateSkills, setPrevCandidateSkills] = useState(
    candidate?.skills
  );
  if (candidate?.skills !== prevCandidateSkills) {
    setPrevCandidateSkills(candidate?.skills);
    setSkills(candidate?.skills || []);
  }

  const handleAdd = (skillData) => {
    if (typeof skillData === "object" && skillData.isCustom) {
      setSkills([skillData, ...skills]);
    } else {
      setSkills([{ id: "new", name: "Saving..." }, ...skills]);
    }
    onAdd(skillData);
  };

  const handleRemove = (skillId) => {
    setSkills(skills.filter((skill) => skill.id !== skillId));
    if (typeof skillId === "number" || !String(skillId).startsWith("custom-")) {
      onRemove(skillId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit skills</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            <Box>Search for skills to add, or remove existing skills.</Box>
            <Box sx={{ mt: 4, mb: 8 }}>
              <SkillSelect exclude={candidate?.skills?.map((skill) => skill.id) || []} onChange={handleAdd} />
            </Box>
            {open && (
              <SkillsGrid>
                {skills.map((skill) => (
                  <Skill key={skill.id}>
                    <Pill className={skill.id === "new" ? "new-item" : ""}>
                      {skill.logo && (
                        <SkillLogo sx={{ width: 20, height: 20 }} src={skill.logo} variant="square" title={skill.name} alt={skill.name} />
                      )}
                      <Description variant="body2">{skill.name}</Description>
                      <CloseButton size="small" onClick={() => handleRemove(skill.id)}>
                        <CloseIcon />
                      </CloseButton>
                    </Pill>
                  </Skill>
                ))}
                {skills.length === 0 && (
                  <Box sx={{ mt: 2, mb: 2, fontStyle: "italic" }}>No skills selected</Box>
                )}
              </SkillsGrid>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
